import { useMemo, useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { CellClickedEvent, CellValueChangedEvent } from 'ag-grid-enterprise'
import { Plus } from 'lucide-react'

import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { Button } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { UploadTrack } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { AddUploadTrackEntryModal } from '@/components/dialogs/AddUploadTrackEntryModal'
import { PM } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import { useUpdateInstrumentsMutation } from '@/react-query/mutations/upload-track'

const spotColDefs = createColDefs([
  {
    field: 'ticker',
    headerName: 'Ticker',
    width: 125,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'asatdate',
    headerName: 'Date',
    width: 125,
    cellStyle: { textAlign: 'center' },
  },
  {
    field: 'spot',
    headerName: 'Price',
    width: 125,
    cellStyle: { textAlign: 'center' },
    valueFormatter: numberFormatter({
      delimiter: true,
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }),
  },
])

const getInstrumentsColDefs = (canEdit: boolean) => {
  return createColDefs([
    { field: 'id' },
    { field: 'bbgticker' },
    { field: 'ticker' },
    { field: 'ccy', editable: canEdit },
    { field: 'leveragefactor', editable: canEdit },
    { field: 'assetclass', editable: canEdit },
    { field: 'factor', editable: canEdit },
    { field: 'subfactor', editable: canEdit },
    { field: 'axeflag', editable: canEdit },
    { field: 'region', editable: canEdit },
    { field: 'issuercode', editable: canEdit },
    { field: 'isin', editable: canEdit },
    { field: 'name', editable: canEdit },
    { field: 'tenormonths', editable: canEdit },
    { field: 'isindefaultccy', editable: canEdit },
    { field: 'tickerindefaultccy', editable: canEdit },
    { field: 'isindex', editable: canEdit },
    { field: 'ccy2', editable: canEdit },
    { field: 'isfx', editable: canEdit },
    { field: 'isrates', editable: canEdit },
    { field: 'isequity', editable: canEdit },
    { field: 'iscds', editable: canEdit },
    { field: 'curveid', editable: canEdit },
    { field: 'ratestype', editable: canEdit },
    { field: 'isinrfq', editable: canEdit },
    { field: 'iscommodity', editable: canEdit },
    { field: 'isbond', editable: canEdit },
    { field: 'isinflation', editable: canEdit },
  ])
}

export interface UploadTrackPageProps {}

export const UploadTrackPage: React.FC<UploadTrackPageProps> = ({}) => {
  const { hasPermissions } = useUserContext()

  const hasWritePermissions = hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_UPLOAD_TRACK],
  }))

  const [adding, setAdding] = useState(false)
  const [ticker, setTicker] = useState('SPXUSD')

  const updateInstrumentsDataMutation = useUpdateInstrumentsMutation()

  const spotParams: UploadTrack.GetMarketdataSpotParams = {
    ticker,
  }

  const instrumentsParams: UploadTrack.GetInstrumentsParams = {
    input_type: 'structurename',
    value: null,
  }

  const api = useApi()

  const queries = useQueries({
    queries: [
      {
        queryKey: ['upload-track', 'spot-data', spotParams],
        queryFn: () => api.uploadTrack.getMarketdataSpot(spotParams),
      },
      {
        queryKey: ['upload-track', 'instrument-data', instrumentsParams],
        queryFn: () => api.uploadTrack.getInstruments(instrumentsParams),
      },
    ],
  })

  const [spot, instruments] = queries

  const [spotData, instrumentsData] = useMemo(() => {
    return [spot.data ?? [], instruments.data ?? []]
  }, [spot, instruments])

  return (
    <Page metadata={{ title: 'Upload Track' }} permissions={PM.UPLOAD_TRACK.permissions}>
      <AddUploadTrackEntryModal open={adding} onOpenChange={setAdding} />
      <CanAccess permissions={[PERMISSIONS.WRITE_UPLOAD_TRACK]}>
        <HeaderConfigBar>
          <div className="flex items-center gap-2">
            <Button size="sm" variant={'outline'} onClick={() => setAdding(true)}>
              <Plus />
              Add an entry
            </Button>
          </div>
        </HeaderConfigBar>
      </CanAccess>
      <ResizablePanelGroup direction={'horizontal'}>
        <ResizablePanel>
          <Widget exposed className={'h-full'}>
            <WidgetHeaderWithQuery title={'Marketdata.Spot Prod table entries'} query={spot} />
            <WidgetContent loading={spot.isLoading}>
              <CustomAgGrid columnDefs={spotColDefs} rowData={spotData} domLayout={'normal'} />
            </WidgetContent>
          </Widget>
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel>
          <Widget exposed className={'h-full'}>
            <WidgetHeaderWithQuery
              title={'Marketdata.Instrument Prod table entries (refresh 1min)'}
              query={instruments}
            />
            <WidgetContent loading={instruments.isLoading}>
              <CustomAgGrid
                columnDefs={getInstrumentsColDefs(hasWritePermissions)}
                rowData={instrumentsData}
                onCellValueChanged={(
                  params: CellValueChangedEvent<UploadTrack.GetInstrumentsData[number]>,
                ) => {
                  return updateInstrumentsDataMutation.mutateAsync({
                    rowData: params.data,
                    oldValue: params.oldValue,
                    newValue: params.newValue,
                    updatedField: params.colDef.field!,
                  })
                }}
                onCellClicked={(
                  params: CellClickedEvent<UploadTrack.GetInstrumentsData[number]>,
                ) => {
                  setTicker(params.data?.ticker!)
                }}
                domLayout={'normal'}
              />
            </WidgetContent>
          </Widget>
        </ResizablePanel>
      </ResizablePanelGroup>
    </Page>
  )
}
